import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import OtpInput from "@/components/base/OtpInput";

export default function BusinessAuthPage() {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp, isAuthenticated, loading, user, logout } = useAuth();
  const [step, setStep] = useState<"form" | "otp">("form");
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [email, setEmail] = useState("");

  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      if (user.roles.includes("business") || user.roles.includes("admin")) {
        navigate("/doanh-nghiep", { replace: true });
      }
    }
  }, [loading, isAuthenticated, navigate, user]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const backToForm = () => {
    setStep("form");
    setOtpCode("");
    setFormError("");
    setFormSuccess("");
  };

  const startResendTimer = () => setResendTimer(60);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim()) {
      setFormError("Vui lòng nhập email doanh nghiệp.");
      return;
    }
    if (!emailRegex.test(email.trim())) {
      setFormError("Email không đúng định dạng.");
      return;
    }

    setSubmitting(true);
    try {
      await sendOtp(email.trim());
      setOtpEmail(email.trim());
      setOtpCode("");
      setStep("otp");
      setFormSuccess("");
      startResendTimer();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Có lỗi xảy ra. Vui lòng thử lại.";
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (otpCode.length < 6) {
      setFormError("Vui lòng nhập đầy đủ 6 chữ số của mã OTP.");
      return;
    }

    setSubmitting(true);
    try {
      const appUser = await verifyOtp(otpEmail, otpCode, "business");

      if (!appUser) {
        setFormError("Không thể xác thực tài khoản. Vui lòng thử lại.");
        return;
      }

      if (appUser.roles.length === 0) {
        await logout();
        setFormError("Tài khoản chưa được thiết lập đúng cách. Vui lòng liên hệ hỗ trợ.");
        return;
      }

      if (!appUser.roles.includes("business") && !appUser.roles.includes("admin")) {
        await logout();
        setFormError("Tài khoản này không phải tài khoản doanh nghiệp. Vui lòng đăng nhập tại trang dành cho Người tìm việc.");
        return;
      }

      navigate("/doanh-nghiep", { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Có lỗi xảy ra. Vui lòng thử lại.";
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setFormError("");
    setFormSuccess("");
    setSubmitting(true);
    try {
      await sendOtp(otpEmail);
      startResendTimer();
      setFormSuccess("Đã gửi lại mã OTP mới. Vui lòng kiểm tra email.");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Có lỗi xảy ra. Vui lòng thử lại.";
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <p className="text-sm text-foreground-500">Đang tải...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20Vietnamese%20small%20business%20office%20interior%20with%20warm%20natural%20lighting%2C%20diverse%20team%20meeting%20in%20bright%20collaborative%20workspace%2C%20clean%20white%20walls%20with%20warm%20wood%20and%20green%20plant%20accents%2C%20professional%20yet%20friendly%20atmosphere%2C%20hopeful%20optimistic%20mood%2C%20editorial%20photography%20style%2C%20soft%20natural%20light%20through%20windows%2C%20inspiring%20and%20welcoming%20environment%20for%20entrepreneurs%20and%20business%20owners&width=1400&height=1800&seq=biz-auth-hero-v3&orientation=portrait"
          alt="Không gian làm việc doanh nghiệp hiện đại"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/35 to-black/30"></div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="mb-10">
            <Link to="/" className="flex items-center gap-2 no-underline mb-12">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur rounded-lg">
                <i className="ri-briefcase-line text-white text-xl"></i>
              </div>
              <span className="font-heading font-bold text-xl text-white">
                Vietnam<span className="text-white/80">.cv</span>
              </span>
            </Link>

            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 backdrop-blur border border-green-400/30 rounded-full mb-6 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
              <i className="ri-shield-check-line text-green-300 text-lg"></i>
              <span className="text-green-200 text-sm font-semibold tracking-wide">HOÀN TOÀN MIỄN PHÍ CHO DOANH NGHIỆP</span>
            </div>

            <h1 className="font-heading text-4xl leading-tight font-bold mb-5 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
              Đăng tin tuyển dụng<br />miễn phí, không giới hạn
            </h1>
            <p className="text-white/75 text-base leading-relaxed max-w-md animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
              Tiếp cận hàng ngàn ứng viên tiềm năng mỗi ngày. Đăng nhập tài khoản doanh nghiệp để quản lý tin tuyển dụng và hồ sơ ứng viên ngay hôm nay.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3 animate-slide-in-left" style={{ animationDelay: "0.4s" }}>
              <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-base"></i>
              </div>
              <span className="text-white/85 text-sm">Đăng tin tuyển dụng không giới hạn số lượng</span>
            </div>
            <div className="flex items-center gap-3 animate-slide-in-left" style={{ animationDelay: "0.5s" }}>
              <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-base"></i>
              </div>
              <span className="text-white/85 text-sm">Tiếp cận 12.000+ ứng viên tiềm năng mỗi tháng</span>
            </div>
            <div className="flex items-center gap-3 animate-slide-in-left" style={{ animationDelay: "0.6s" }}>
              <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-base"></i>
              </div>
              <span className="text-white/85 text-sm">Quản lý hồ sơ ứng viên dễ dàng, trực quan</span>
            </div>
            <div className="flex items-center gap-3 animate-slide-in-left" style={{ animationDelay: "0.7s" }}>
              <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-base"></i>
              </div>
              <span className="text-white/85 text-sm">Hoàn toàn miễn phí - không phí ẩn, không giới hạn thời gian</span>
            </div>
          </div>

          <div className="flex gap-6 mt-10 animate-fade-in" style={{ animationDelay: "0.8s" }}>
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-white">12.000+</div>
              <div className="text-sm text-white/70 mt-1">Việc làm</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-white">3.500+</div>
              <div className="text-sm text-white/70 mt-1">Công ty</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-white">34</div>
              <div className="text-sm text-white/70 mt-1">Tỉnh thành</div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center animate-fade-in-up">
            <Link to="/" className="inline-flex items-center gap-2 no-underline">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-briefcase-line text-white text-xl"></i>
              </div>
              <span className="font-heading font-bold text-xl text-foreground-950">
                Vietnam<span className="text-primary-500">.cv</span>
              </span>
            </Link>
          </div>

          <div className="lg:hidden mb-6 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full">
              <i className="ri-shield-check-line text-green-600 text-base"></i>
              <span className="text-green-700 text-xs font-semibold">HOÀN TOÀN MIỄN PHÍ CHO DOANH NGHIỆP</span>
            </div>
          </div>

          {step === "otp" ? (
            <>
              <div className="mb-8 animate-fade-in-up">
                <button
                  onClick={backToForm}
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer mb-4"
                >
                  <i className="ri-arrow-left-line text-base"></i>
                  <span>Đổi email</span>
                </button>
                <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-2xl mb-5">
                  <i className="ri-mail-check-line text-primary-500 text-2xl"></i>
                </div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-1.5">
                  Nhập mã xác thực
                </h2>
                <p className="text-sm text-foreground-600">
                  Chúng tôi đã gửi mã xác thực 6 chữ số đến{" "}
                  <span className="font-semibold text-foreground-900">{otpEmail}</span>. Vui lòng kiểm tra hộp thư (bao gồm cả thư rác).
                </p>
              </div>

              <form onSubmit={handleVerify} className="space-y-5 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
                <OtpInput value={otpCode} onChange={setOtpCode} />

                {formError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <i className="ri-error-warning-line text-base flex-shrink-0"></i>
                    <span>{formError}</span>
                  </div>
                )}

                {formSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
                    <i className="ri-checkbox-circle-line text-base flex-shrink-0"></i>
                    <span>{formSuccess}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || otpCode.length < 6}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      <span>Đang xác thực...</span>
                    </>
                  ) : (
                    "Xác nhận & đăng nhập"
                  )}
                </button>

                <div className="text-center text-sm text-foreground-500">
                  <span>Không nhận được mã? </span>
                  {resendTimer > 0 ? (
                    <span className="text-foreground-400">
                      Gửi lại sau {resendTimer}s
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={submitting}
                      className="text-primary-600 font-semibold hover:text-primary-700 cursor-pointer disabled:opacity-60"
                    >
                      Gửi lại mã
                    </button>
                  )}
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="mb-8 animate-fade-in-up">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-1.5">
                  Đăng nhập doanh nghiệp
                </h2>
                <p className="text-sm text-foreground-600">
                  Đăng nhập để quản lý tin tuyển dụng và ứng viên
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Email doanh nghiệp
                  </label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="doanhnghiep@example.com"
                      className="w-full pl-10 pr-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>
                </div>

                {formError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 animate-fade-in-up">
                    <i className="ri-error-warning-line text-base flex-shrink-0"></i>
                    <span>{formError}</span>
                  </div>
                )}

                {formSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 animate-fade-in-up">
                    <i className="ri-checkbox-circle-line text-base flex-shrink-0"></i>
                    <span>{formSuccess}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 animate-fade-in-up" style={{ animationDelay: "0.3s" }}
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      <span>Đang gửi mã...</span>
                    </>
                  ) : (
                    "Gửi mã xác thực"
                  )}
                </button>

                <div className="flex items-center gap-2 justify-center text-xs text-foreground-400 pt-1 animate-fade-in-up" style={{ animationDelay: "0.32s" }}>
                  <i className="ri-shield-check-line text-base"></i>
                  <span>Đăng nhập không cần mật khẩu bằng mã OTP</span>
                </div>
              </form>

              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-2xl animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-shield-check-line text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-800 mb-1">Dịch vụ hoàn toàn miễn phí</p>
                    <p className="text-xs text-green-700 leading-relaxed">
                      Không thu phí đăng tin, không giới hạn số lượng tin tuyển dụng, không ràng buộc thời gian. Chúng tôi cam kết mang đến nền tảng tuyển dụng chất lượng và hoàn toàn miễn phí cho mọi doanh nghiệp.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}